import { markLaunchReady } from './launchScreen';

/**
 * Первый экран приложения: когда маршрутизатор его отрисовал, экран запуска гаснет.
 *
 * Раньше сплэш держался до этого же момента, и человек около 2,3 с смотрел на сплэш, а на
 * Android 12 и MIUI - на пустой серый экран (там система нашу тему не берёт). Теперь сплэш
 * снимает скрипт оболочки сразу после её первого кадра, а до первого экрана на месте наш экран
 * запуска с 7x7 мини-игрой (`launchScreen.ts`). Здесь он убирается.
 *
 * Сплэш здесь тоже снимается - на случай, если скрипт оболочки не смог (моста Capacitor не
 * оказалось на месте). Повторный `hide()` плагину ничего не стоит. Потолок на случай, если JS
 * вообще не поднялся, стоит в capacitor.config.ts (`launchShowDuration`).
 *
 * Всё закрыто флагом нативной сборки: в веб-сборке и плагин, и экран запуска вырезаются.
 */

/** Кусок маршрутизатора, который здесь нужен. */
type RenderedEvents = {
  subscribe: (eventType: 'onRendered', fn: () => void) => () => void;
};

/**
 * Сколько ждать кадра, прежде чем убирать экран запуска без него.
 *
 * Пока сплэш на экране, Android не даёт окну приложения рисоваться (так плагин его и держит), и
 * WebView под ним может не выдать ни одного кадра, а значит, не вызвать requestAnimationFrame.
 * Ждать кадра бесконечно нельзя: экран запуска висел бы до страховки. 100 мс хватает, чтобы стили
 * и раскладка первого экрана успели посчитаться, и почти не видно глазу.
 */
const FRAME_WAIT_MAX_MS = 100;

/**
 * Метка в performance: первый экран нарисован и экран запуска пошёл гаснуть (CDP, трассировка).
 * Снятие сплэша отмечает скрипт оболочки своей меткой, `SPLASH_HIDDEN_MARK`.
 */
export const LAUNCH_READY_MARK = 'launch-screen-ready';

/** Два кадра подряд: первый экран не только собран, но и нарисован. Или потолок. */
export const waitForFirstFrame = (maxMs = FRAME_WAIT_MAX_MS): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const ceiling = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, maxMs);
  });
  const frames = new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });

  return Promise.race([frames, ceiling]).finally(() => clearTimeout(timer));
};

async function hideSplash(): Promise<void> {
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    // Зовём метод прокси, а сам прокси не возвращаем и не ждём: registerPlugin отдаёт Proxy с
    // `.then`, и await на нём уходит в несуществующий нативный метод (см. deviceSettings.ts).
    await SplashScreen.hide();
  } catch {
    // Плагина нет в этой нативной сборке (например, iOS-проект ещё не синхронизирован). Сплэш
    // тогда никто и не держал – снимать нечего.
  }
}

/**
 * Когда маршрутизатор впервые отрисует экран, убрать экран запуска (и сплэш, если он ещё висит),
 * затем вызвать `afterHide`.
 *
 * `onRendered` приходит после того, как загрузка маршрутов закончилась и дерево экрана вмонтировано:
 * в приложении это уже шапка с меню, а не пустая оболочка. Ждать ленту незачем, у неё свой скелет.
 */
export function hideNativeSplashOnFirstRender(
  router: RenderedEvents,
  afterHide?: () => void
): void {
  // Сравнение прямо здесь, а не `envConfig.IS_NATIVE`: флаг из объекта сборщик не сворачивает, и
  // экран запуска (`launchScreen.ts` со всем его CSS) уезжал бы в веб-бандл. Это выражение в вебе
  // становится `false`, тело функции выбрасывается, а с ним и импорт.
  if (import.meta.env.VITE_IS_NATIVE !== 'true') {
    return;
  }

  const unsubscribe = router.subscribe('onRendered', () => {
    unsubscribe();

    void (async () => {
      await waitForFirstFrame();
      markLaunchReady();
      performance.mark?.(LAUNCH_READY_MARK);
      await hideSplash();
      afterHide?.();
    })();
  });
}
